import { useFormikContext } from "formik";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import {
  ICreateProposalForm,
  ICreateProposalProps,
} from "../../../../common/interfaces/proposals.interface";
import WizardField from "../../../WizardField/WizardField";
import "./WithdrawalProposal.scss";
import { MESSAGE_TYPE } from "../../../../common/constants/common.constants";
import { Governance, ProgramAccount } from "@solana/spl-governance";
import { createNotification } from "../../../../utilities/notification";
import {
  getMaxDecimalsLengthPerMint,
  parseSecondsToDays,
} from "../../../../utilities/helpers";
import { EXECUTE_WITHDRAWAL_FEE } from "../../../../common/constants/fees";
import MintSelect from "../TransferProposal/MintSelect/MintSelect";
import { NATIVE_MINT } from "@solana/spl-token";
import { clubsStore } from "../../../../contexts/clubStore";

const WithdrawalProposal: FC<ICreateProposalProps> = ({ formFields }) => {
  const { values, setFieldValue } = useFormikContext<ICreateProposalForm>();
  const [governanceInfo, setGovernanceInfo] =
    useState<ProgramAccount<Governance>>();
  const { treasury, clubBasicInfo } = clubsStore();

  useEffect(() => {
    void getGovernanceInfo();
  }, [values.withdrawAmount, treasury]);

  useEffect(() => {
    setFieldValue("mint", treasury?.denominatedTokenInfo?.mint ?? NATIVE_MINT);
  }, []);

  const getGovernanceInfo = useCallback(async () => {
    try {
      if (treasury) {
        const governancePubKey = treasury.withdrawalGovernance
          ? treasury.withdrawalGovernance.toString()
          : treasury.governance.toString();
        setGovernanceInfo(
          clubBasicInfo.governances.find(
            (item) => item.pubkey.toString() === governancePubKey
          )
        );
      }
    } catch (error) {
      console.log(error);
      createNotification(MESSAGE_TYPE.ERROR, "Can not read governance data");
    }
  }, [treasury]);

  const getMaxVotingTimeForWithdrawalProposal = useMemo(() => {
    if (governanceInfo)
      return parseSecondsToDays(governanceInfo.account.config.maxVotingTime);
  }, [governanceInfo]);

  const totalSolWIthdrawableAmount = useMemo(() => {
    let totalWIthdrawableAmount = 0;
    if (!treasury) return;
    if (treasury.treasuryAmount > 0)
      totalWIthdrawableAmount =
        treasury.treasuryAmount - EXECUTE_WITHDRAWAL_FEE;
    else totalWIthdrawableAmount = treasury.treasuryAmount;
    return totalWIthdrawableAmount > 0 ? totalWIthdrawableAmount : 0;
  }, [treasury]);

  return (
    <div className="withdrawal-proposal">
      <div className="withdrawal-proposal__main-info">
        <div className="withdrawal-proposal__available-amount-info">
          <div>
            <p className="withdrawal-proposal__label">
              Total withdrawable amount
            </p>
            <p className="withdrawal-proposal__max-amount">
              {values.mint.toString() === NATIVE_MINT.toString() &&
              treasury?.denominatedTokenInfo === undefined
                ? totalSolWIthdrawableAmount
                : treasury?.tokenAccounts.find(
                    (item) => item.mint.toString() === values.mint.toString()
                  )?.balance}
            </p>
          </div>
          {/* Not shown for now */}
          {/* {treasury?.denominatedTokenInfo === undefined &&
            values.mint.toString() === NATIVE_MINT.toString() && (
              <div className="withdrawal-proposal__profit-info">
                <p className="withdrawal-proposal__profit-label">
                  Financial Account balance
                </p>
                <p className="withdrawal-proposal__profit-amount">
                  {" "}
                  {treasury?.profitAmount.toLocaleString(
                    undefined,
                    localeStringOptions
                  )}{" "}
                  SOL
                </p>
              </div>
            )} */}
        </div>

        <div className="withdrawal-proposal__mint-and-amount">
          <WizardField
            type="number"
            placeholder="Enter the amount here"
            name={formFields.withdrawAmount.name}
            label={formFields.withdrawAmount.label}
            maxDecimalsLength={getMaxDecimalsLengthPerMint(
              treasury?.denominatedTokenInfo?.decimals
            )}
          />
          {treasury && (
            <MintSelect
              treasuryTokenAccounts={treasury.tokenAccounts}
              isForm
              setFieldValue={setFieldValue}
              chosenMint={treasury.denominatedTokenInfo?.mint.toString()}
            />
          )}
        </div>
      </div>

      {governanceInfo && (
        <div className="withdrawal-proposal__governance-info">
          <div className="withdrawal-proposal__governance-info-item">
            <p className="withdrawal-proposal__governance-label">
              Approval vote percentage
            </p>
            <p className="withdrawal-proposal__governance-value">
              {governanceInfo.account.config.voteThresholdPercentage.value}%
            </p>
          </div>
          <div className="withdrawal-proposal__governance-info-item">
            <p className="withdrawal-proposal__governance-label">
              Max voting time
            </p>
            <p className="withdrawal-proposal__governance-value">
              {getMaxVotingTimeForWithdrawalProposal} days
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default WithdrawalProposal;
